import React from 'react';
import { useTranslation } from 'react-i18next';
import { useOrganization } from './OrganizationContext';
import OrgBrandLogo from './OrgBrandLogo';
import { shouldShowBrandNameWithLogo, isCoursoloBrandName } from './brandLogoUtils';

/**
 * Compact org brand block for auth / public surfaces.
 * Logo carries the brand; never repeat Coursolo.ai when the logo is present.
 */
export default function AuthOrgBrand({ className = '', showTenantHint = true }) {
    const { t } = useTranslation();
    const { branding, organization, loading } = useOrganization();
    const name = branding?.companyName || organization?.name || 'Coursolo';
    const primary = branding?.primaryColor || 'var(--org-primary, #2563eb)';
    const isTenant = Boolean(organization?.tenant && organization?.slug && organization?.slug !== 'coursolo');
    const showName = shouldShowBrandNameWithLogo(branding, name)
        || (isTenant && !isCoursoloBrandName(name));

    if (loading && !branding) {
        return <div className={`h-10 ${className}`} aria-hidden />;
    }

    return (
        <div className={`flex flex-col items-center gap-3 ${className}`}>
            <OrgBrandLogo
                branding={branding}
                className={`${showName ? 'h-12 max-w-[180px]' : 'h-14 sm:h-16 max-w-[220px]'} object-contain`}
                alt={name}
            />
            {showName ? (
                <div className="text-center space-y-0.5">
                    <div className="text-sm font-semibold text-slate-900 dark:text-white">{name}</div>
                    {organization?.slug && isTenant ? (
                        <div className="text-xs text-slate-500 dark:text-slate-400">{organization.slug}</div>
                    ) : null}
                </div>
            ) : null}
            {showTenantHint && isTenant ? (
                <div
                    className="mt-1 px-3 py-1.5 rounded-full text-[11px] font-medium text-slate-700 dark:text-slate-200 bg-slate-100 dark:bg-slate-700/60 border border-slate-200 dark:border-slate-600"
                    style={{ borderColor: primary }}
                >
                    {t('auth.signingInToOrg')}
                </div>
            ) : null}
        </div>
    );
}

export function useOrgAuthUi() {
    const { branding, organization, loading } = useOrganization();
    const primary = branding?.primaryColor || '#2563eb';
    const sso = organization?.sso || {};
    const slug = organization?.slug;
    const isTenantHost = Boolean(organization?.tenant && organization?.id);
    return {
        loading,
        branding,
        organization,
        primary,
        companyName: branding?.companyName || organization?.name || 'Coursolo',
        ssoEnabled: Boolean(sso.enabled),
        ssoEnforce: Boolean(sso.enforce),
        allowSelfSignup: organization?.settings?.allowSelfSignup !== false,
        slug,
        organizationId: organization?.id,
        isTenantHost,
    };
}
